import React, { useContext, createContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { getClientEnvironmentDetails } from '@sm/utils';
import experimentsQuery from './Experiments.graphql';

export type TreatmentItem = {
  treatmentName: string;
  treatmentId: string;
  experimentId: string;
  experimentName: string;
  assignmentType: string;
};

export type Experiment = {
  id: string;
  name: string;
  assignmentType: string;
  treatment: {
    id: string;
    name: string;
  };
};

export type ExperimentsContextType = {
  loading: boolean;
  experiments?: Experiment[];
};

export type UseExperimentArgs = {
  name: string;
  treatmentNames: string[];
};
export type UseExperimentResult = {
  treatments: Record<string, TreatmentItem>;
  name?: string;
  loading: boolean;
};

export const expContext = createContext<ExperimentsContextType>({
  experiments: [],
  loading: true,
});

type ExperimentProviderProps<T extends {}> = Record<string, T>;

const ExperimentProvider = <T extends {}>(
  props: React.PropsWithChildren<ExperimentProviderProps<T>>
) => {
  const { isBrowser } = getClientEnvironmentDetails();
  const pageReferer = isBrowser ? document.referrer : '/';
  const pageUrl = isBrowser ? window.location.href : '/';
  const { data, loading } = useQuery(experimentsQuery, {
    fetchPolicy: 'no-cache',
    variables: { pageReferer, pageUrl },
  });
  const expContextValue: ExperimentsContextType = useMemo(() => {
    return { experiments: (data && data.experiments) || [], loading };
  }, [data, loading]);

  return <expContext.Provider value={expContextValue} {...props} />;
};

function useExperiment({
  name,
  treatmentNames,
}: UseExperimentArgs): UseExperimentResult {
  const context = useContext(expContext);
  const { experiments, loading } = context;
  const baseObj = { treatments: {}, loading };

  // if loading, we want to fire the loading component provide by the user
  if (loading) return baseObj;

  // If the service fails, or the experiments are undefined, we will want to show the control.
  if (!experiments) return baseObj;

  // match the name to the list of experiments that have come back
  const expObj = experiments.find(exp => name === exp.name);
  if (!expObj) return baseObj;

  const notAssigned: Record<string, unknown> = {};
  treatmentNames.forEach(treatment => {
    if (treatment !== expObj.treatment.name) {
      notAssigned[treatment] = {};
    }
  });

  const reduced = Object.keys(expObj).reduce<UseExperimentResult>(
    acc => {
      // eslint-disable-next-line no-param-reassign
      acc = {
        treatments: {
          [expObj.treatment.name]: {
            treatmentName: expObj.treatment.name,
            treatmentId: expObj.treatment.id,
            experimentId: expObj.id,
            experimentName: expObj.name,
            assignmentType: expObj.assignmentType,
          },
        },
        name: expObj.name,
        loading,
      };
      return acc;
    },
    { loading, treatments: {} }
  );
  Object.assign(reduced.treatments, notAssigned);
  return reduced;
}

export { ExperimentProvider, useExperiment };
